import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema, headingsUISchema } from '../Header';
import GrowthChartWrapper from './GrowthChartWrapper';

const schema: JSONSchema7 = {
    title: 'Growth Chart',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        dataKey: {
            type: 'string',
            title: 'Data Key',
            default: '',
        },
        chartType: {
            type: 'string',
            title: 'Chart Type',
            default: 'line',
        },
        constructorType: {
            type: 'string',
            title: 'Chart Constructor Type',
            default: 'stockChart',
        },
        showDateInput: {
            type: 'boolean',
            title: 'Show Date Input',
            default: false,
        },
        showInvestmentAmount: {
            type: 'boolean',
            title: 'Show investment amount input field',
            description: 'This options will enable an investment amount',
            default: false,
        },
        showChartRange: {
            type: 'boolean',
            title: 'Show highChart range',
            // eslint-disable-next-line max-len
            description: 'This option will enable "start date", "end date", and "range" fields',
            default: false,
        },
        showTotalReturns: {
            type: 'boolean',
            title: 'Show total return',
            description: 'This option will show the total return for a given timeframe',
            default: false,
        },
        showDifferentZone: {
            type: 'boolean',
            title: 'Divide chart in zone',
            description: 'This option will show different zone on chart with diffrent color',
            default: false,
        },
        transformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        chartTitles: headingsSchema,
        ...customSchemas.highchartOptionsSchema.HighchartOptionsSchema.schema(),
        noDataMessage: {
            type: 'string',
            title: 'Text to show when no data',
        },
        growthChartAltText: {
            type: 'string',
            title: 'Alternate text to show for screen readers',
        },
        rangeButtons: {
            type: 'string',
            title: 'Chart Range Buttons',
            description: 'Enter json array of highchart button objects',
            default: '',
        },
        defaultSelectedRange: {
            type: 'number',
            title: 'Default Range Selector',
        },
        toolTipValueFormat: {
            type: 'string',
            title: 'Format for tooltip value',
            default: 'curMax2',
        },
        toolTipDateFormat: {
            type: 'string',
            title: 'Format for tooltip date',
            default: 'dateMed',
        },
        customTooltipOptions: {
            type: 'boolean',
            title: 'Add tooltip options (if more than one value needs to be shown in tooltip)',
            default: false,
        },
        isLimitToTenYears: {
            type: 'boolean',
            title: 'Display last 10 years',
            default: false,
        },
        applyMaxDateValue: {
            type: 'boolean',
            title: 'Apply max date value',
            default: false,
        },
        applyCustomYaxisLabel: {
            type: 'boolean',
            title: 'Use custom Y axis label',
            default: false,
        },
        xAxislabelFormat: {
            type: 'string',
            title: 'Format for X-axis label (supported values: monthYear)',
            default: '',
        },
        isShowMultipleSeries: {
            type: 'boolean',
            title: 'Show multiple series',
            default: false,
        },
        useSimpleDataset: {
            type: 'boolean',
            title: 'Use simple dataset',
            default: false,
        },
        yAxisValueOverride: {
            type: 'boolean',
            title: 'Y axis value to be not modified',
            default: false,
        },
    },
    dependencies: {
        showInvestmentAmount: {
            oneOf: [
                {
                    properties: {
                        showInvestmentAmount: { enum: [true] },
                        growthAmountLabel: {
                            type: 'string',
                            title: 'Growth Amount Label',
                            default: 'Investment amount ($):',
                        },
                        initialGrowthAmount: {
                            type: 'number',
                            title: 'Initial Growth Amount',
                            default: 10000,
                        },
                    },
                    required: ['growthAmountLabel'],
                },
                {
                    properties: {
                        showInvestmentAmount: { enum: [false] },
                    },
                },
            ],
        },
        showTotalReturns: {
            oneOf: [
                {
                    properties: {
                        showTotalReturns: { enum: [true] },
                        totalReturnsLabel: {
                            type: 'string',
                            title: 'Total returns label',
                            default: 'Total return for this timeframe',
                        },
                    },
                    required: ['totalReturnsLabel'],
                },
                {
                    properties: {
                        showTotalReturns: { enum: [false] },
                    },
                },
            ],
        },
        showDifferentZone: {
            oneOf: [
                {
                    properties: {
                        showDifferentZone: { enum: [true] },
                        zoneOptions: {
                            type: 'object',
                            properties: {
                                year: {
                                    type: 'string',
                                    title: 'Start zone till year',
                                    default: '01-01-2016',
                                    description: 'Zone from min date in data to this year',
                                },
                                color: {
                                    type: 'string',
                                    title: 'Color',
                                    default: '#b74a02',
                                    description: 'Color for the chart line',
                                },
                            },
                        },
                    },
                },
                {
                    properties: {
                        showDifferentZone: { enum: [false] },
                    },
                },
            ],
        },
        transformData: {
            oneOf: [
                {
                    properties: {
                        transformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default: 'return (data) => { return data }',
                        },
                    },
                },
                {
                    properties: {
                        transformData: { enum: [false] },
                    },
                },
            ],
        },
        applyCustomYaxisLabel: {
            oneOf: [
                {
                    properties: {
                        applyCustomYaxisLabel: { enum: [true] },
                        yAxislabelFormat: {
                            type: 'string',
                            title: 'Format for Y-axis label',
                            default: 'curMax0',
                        },
                    },
                },
                {
                    properties: {
                        applyCustomYaxisLabel: { enum: [false] },
                    },
                },
            ],
        },
        showDateInput: {
            oneOf: [
                {
                    properties: {
                        showDateInput: { enum: [true] },
                        datePicker: {
                            type: 'string',
                            title: 'Select date picker type',
                            enum: [
                                'Isolated Date Picker',
                                'Integrated Date Picker',
                                'Custom Date Picker',
                            ],
                            default: 'Isolated Date Picker',
                            description: `Isolated Date Picker has seperate year,month,day picker and 
                            Integrated Date Picker has one input for all.`,
                        },
                    },
                    dependencies: {
                        datePicker: {
                            oneOf: [
                                {
                                    properties: {
                                        datePicker: { enum: ['Isolated Date Picker'] },
                                        datePickerOptions: {
                                            title: 'Date Picker Options',
                                            type: 'object',
                                            required: [],
                                            properties: {
                                                startDateLabel: {
                                                    type: 'string',
                                                    title: 'Enter start date label',
                                                    default: '',
                                                },
                                                endDateLabel: {
                                                    type: 'string',
                                                    title: 'Enter end date label',
                                                    default: '',
                                                },
                                                startDayAriaLabel: {
                                                    type: 'string',
                                                    title: 'Start day aria label',
                                                },
                                                startMonthAriaLabel: {
                                                    type: 'string',
                                                    title: 'Start month aria label',
                                                },
                                                startYearAriaLabel: {
                                                    type: 'string',
                                                    title: 'Start year aria label',
                                                },
                                                endDayAriaLabel: {
                                                    type: 'string',
                                                    title: 'End day aria label',
                                                },
                                                endMonthAriaLabel: {
                                                    type: 'string',
                                                    title: 'End month aria label',
                                                },
                                                endYearAriaLabel: {
                                                    type: 'string',
                                                    title: 'End year aria label',
                                                },
                                            },
                                        },
                                    },
                                },
                                {
                                    properties: {
                                        datePicker: {
                                            enum: ['Integrated Date Picker'],
                                        },
                                        integratedDatePickerOptions: {
                                            title: 'Integrated Date Picker Options',
                                            type: 'object',
                                            required: [],
                                            properties: {
                                                datePickerIconPosition: {
                                                    type: 'string',
                                                    title: 'Date Picker Icon position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Right',
                                                },
                                                datePickerIconType: {
                                                    type: 'string',
                                                    title: 'Date Picker Icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                                dateFormat: {
                                                    type: 'string',
                                                    title: 'Enter date format',
                                                    default: 'MMMM d, yyyy',
                                                    description: 'for example- MMMM d, yyyy',
                                                },
                                                startDateLabel: {
                                                    type: 'string',
                                                    title: 'Enter start date label',
                                                    default: 'Start date',
                                                },
                                                endDateLabel: {
                                                    type: 'string',
                                                    title: 'Enter end date label',
                                                    default: 'End date',
                                                },
                                            },
                                            dependencies: {
                                                datePickerIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                datePickerIconType: {
                                                                    enum: [`FontAwesome Icon`],
                                                                },
                                                                datePickerIcon: {
                                                                    type: 'string',
                                                                    title: `Date 
                                                                    Picker Icon name`,
                                                                    default: 'fa-calendar',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                datePickerIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                datePickerImage: {
                                                                    type: 'string',
                                                                    title: `
                                                                        Date Picker
                                                                        Image url`,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                        },
                                    },
                                },
                                {
                                    properties: {
                                        datePicker: { enum: ['Custom Date Picker'] },
                                        customDatePickerOptions: {
                                            title: 'Custom Date Picker Options',
                                            type: 'object',
                                            required: [],
                                            properties: {
                                                startDateFilterByKey: {
                                                    type: 'string',
                                                    title: 'Enter filter by key for start date',
                                                    description:
                                                        'Key value for filter used in query',
                                                },
                                                endDateFilterByKey: {
                                                    type: 'string',
                                                    title: 'Enter filter by key for end date',
                                                    description:
                                                        'Key value for filter used in query',
                                                },
                                                datePickerIconPosition: {
                                                    type: 'string',
                                                    title: 'Date Picker Icon position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Right',
                                                },
                                                datePickerIconType: {
                                                    type: 'string',
                                                    title: 'Date Picker Icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                                closeDatePickerIconPosition: {
                                                    type: 'string',
                                                    title: 'Date Picker Close Icon position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Right',
                                                },
                                                closeDatePickerIconType: {
                                                    type: 'string',
                                                    title: 'Date Picker Close Icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                                backArrowIconType: {
                                                    type: 'string',
                                                    title: 'Back Arrow Icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                                forwardArrowIconType: {
                                                    type: 'string',
                                                    title: 'Back Arrow Icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                                startDatelabel: {
                                                    type: 'string',
                                                    title: 'Enter start date label',
                                                    default: 'Start date',
                                                },
                                                endDatelabel: {
                                                    type: 'string',
                                                    title: 'Enter end date label',
                                                    default: 'End date',
                                                },
                                                dataKey: {
                                                    type: 'string',
                                                    title: 'Enter data key',
                                                    // eslint-disable-next-line max-len
                                                    description: `Enter data key to find minimum and maximum dates 
                                                    from data for default start and end dates.
                                                    Note: date must be in yyyy-mm-dd format`,
                                                },
                                                limitDatekeySelection: {
                                                    type: 'string',
                                                    // eslint-disable-next-line max-len
                                                    title: 'Enter date key for limit date selection',
                                                    // eslint-disable-next-line max-len
                                                    description: `Enter data key to only select certain dates 
                                                    from the data either as a date or dates array.
                                                    Note: date must be in yyyy-mm-dd format`,
                                                },
                                                limitStartDateMinToMinDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit start date minimum value to data's minimum date value`,
                                                },
                                                limitStartDateMaxToCurrentDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit start date maximum to current date`,
                                                },
                                                limitStartDateMaxToMaxDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit start date maximum to data's maximum date value`,
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        'Note-this will only work if "Limit start date to current date" is set to false',
                                                },
                                                limitEndDateMinToStartDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit end date minimum value to start date value`,
                                                },
                                                limitCurrentDaySelection: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit current date selection`,
                                                },
                                                limitEndDateMaxToCurrentDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: 'Limit end date maximum value to current date',
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        'Note-this will only work if "Limit current date selection" is set to false',
                                                },
                                                limitEndDateMaxToMaxDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit end date maximum value to data's maximum date value`,
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        `Note-this will only work if "Limit current date selection" 
                                                        and "Limit end date to current date" is set to false`,
                                                },
                                                limitCurToMaxDate: {
                                                    type: 'boolean',
                                                    // eslint-disable-next-line max-len
                                                    title: `Limit end date maximum value to data's maximum date value`,
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        `Note-this will only work if "Limit end date to current date" is 
                                                        set to false and "Limit current date selection" is set to false`,
                                                },
                                                startDateErrorMessage: {
                                                    type: 'string',
                                                    title: 'Enter start date error message',
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        'Start date error message when its value later than to end date',
                                                },
                                                endDateErrorMessage: {
                                                    type: 'string',
                                                    title: 'Enter end date error message',
                                                    description:
                                                        // eslint-disable-next-line max-len
                                                        'End date error message when its value earlier than start date',
                                                },
                                                limitDateStartErrorMessage: {
                                                    type: 'string',
                                                    title: 'Enter limit start date error message',
                                                },
                                                limitDateEndErrorMessage: {
                                                    type: 'string',
                                                    title: 'Enter limit end date error message',
                                                },
                                                errorIconPosition: {
                                                    type: 'string',
                                                    title: 'Error icon position',
                                                    enum: ['Right', 'Left'],
                                                    default: 'Right',
                                                },
                                                errorIconType: {
                                                    type: 'string',
                                                    title: 'Choose error icon type',
                                                    enum: ['FontAwesome Icon', 'Image'],
                                                    default: 'FontAwesome Icon',
                                                },
                                            },
                                            dependencies: {
                                                datePickerIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                datePickerIconType: {
                                                                    enum: [`FontAwesome Icon`],
                                                                },
                                                                datePickerIcon: {
                                                                    type: 'string',
                                                                    title: `Date 
                                                                    Picker Icon name`,
                                                                    default: 'fa-calendar',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                datePickerIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                datePickerImage: {
                                                                    type: 'string',
                                                                    title: `
                                                                        Date Picker
                                                                        Image url`,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                                errorIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                errorIconType: {
                                                                    enum: ['FontAwesome Icon'],
                                                                },
                                                                errorIcon: {
                                                                    type: 'string',
                                                                    title: 'Enter dropdown search icon name',
                                                                    default:
                                                                        'fa-exclamation-circle',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                errorIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                errorImageUrl: {
                                                                    type: 'string',
                                                                    title: 'Enter dropdown search image url',
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                                closeDatePickerIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                closeDatePickerIconType: {
                                                                    enum: [`FontAwesome Icon`],
                                                                },
                                                                closeDatePickerIcon: {
                                                                    type: 'string',
                                                                    title: `Date 
                                                                    Picker Close Icon name`,
                                                                    default: 'fa-times',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                closeDatePickerIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                closeDatePickerImage: {
                                                                    type: 'string',
                                                                    title: `
                                                                        Date Picker Close
                                                                        Image url`,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                                backArrowIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                backArrowIconType: {
                                                                    enum: [`FontAwesome Icon`],
                                                                },
                                                                backArrowIcon: {
                                                                    type: 'string',
                                                                    title: `Back Arrow Icon name`,
                                                                    default: 'fa-arrow-left',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                backArrowIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                backArrowImage: {
                                                                    type: 'string',
                                                                    title: `
                                                                        Back Arrow
                                                                        Image url`,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                                forwardArrowIconType: {
                                                    oneOf: [
                                                        {
                                                            properties: {
                                                                forwardArrowIconType: {
                                                                    enum: [`FontAwesome Icon`],
                                                                },
                                                                forwardArrowIcon: {
                                                                    type: 'string',
                                                                    title: `Forward Arrow Icon name`,
                                                                    default: 'fa-arrow-right',
                                                                },
                                                            },
                                                        },
                                                        {
                                                            properties: {
                                                                forwardArrowIconType: {
                                                                    enum: ['Image'],
                                                                },
                                                                forwardArrowImage: {
                                                                    type: 'string',
                                                                    title: `
                                                                        Forward Arrow
                                                                        Image url`,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        showDateInput: { enum: [false] },
                    },
                },
            ],
        },
        isShowMultipleSeries: {
            oneOf: [
                {
                    properties: {
                        isShowMultipleSeries: { enum: [true] },
                        multipleSeriesColors: {
                            type: 'array',
                            title: 'Colors for multiple series',
                            items: {
                                type: 'string',
                                default: '#000000',
                            },
                        },
                        multipleSeriesMaxLength: {
                            type: 'number',
                            title: 'Maximum number of series',
                            default: 4,
                        },
                    },
                },
                {
                    properties: {
                        isShowMultipleSeries: { enum: [false] },
                    },
                },
            ],
        },
        customTooltipOptions: {
            oneOf: [
                {
                    properties: {
                        customTooltipOptions: { enum: [true] },
                        tooltipOptions: {
                            type: 'array',
                            title: 'Custom tooltip Options',
                            items: {
                                properties: {
                                    label: { type: 'string', title: 'Label' },
                                    mappingkey: { type: 'string', title: 'Mapping Key' },
                                    format: { type: 'string', title: 'Format' },
                                    className: { type: 'string', title: 'Class Name' },
                                    posChangeClassName: {
                                        type: 'string',
                                        title: 'Positive Change Class Name',
                                    },
                                    negChangeClassName: {
                                        type: 'string',
                                        title: 'Negative Change Class Name',
                                    },
                                    isValueKey: {
                                        type: 'boolean',
                                        title: 'Is value key',
                                    },
                                    seriesNumber: {
                                        type: 'number',
                                        title: 'Series Number',
                                    },
                                    hideFirstTooltip: {
                                        type: 'boolean',
                                        title: 'Hide for first tooltip',
                                        default: false,
                                    },
                                },
                            },
                        },
                    },
                },
                {
                    properties: {
                        customTooltipOptions: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'dataKey',
            'chartType',
            'constructorType',
            'showInvestmentAmount',
            'growthAmountLabel',
            'initialGrowthAmount',
            'showChartRange',
            'showTotalReturns',
            'totalReturnsLabel',
            'showDifferentZone',
            'zoneOptions',
            'chartTitles',
            'options',
            'noDataMessage',
            'growthChartAltText',
            'rangeButtons',
            'defaultSelectedRange',
            'showDateInput',
            'datePicker',
            'datePickerOptions',
            'integratedDatePickerOptions',
            'customDatePickerOptions',
            'toolTipValueFormat',
            'toolTipDateFormat',
            'customTooltipOptions',
            'tooltipOptions',
            'isLimitToTenYears',
            'applyMaxDateValue',
            'applyCustomYaxisLabel',
            'yAxislabelFormat',
            'xAxislabelFormat',
            'isShowMultipleSeries',
            'useSimpleDataset',
            'yAxisValueOverride',
            'multipleSeriesColors',
            'multipleSeriesMaxLength',
        ],
        'Transform Data': ['transformData', 'functionToTransform'],
    },
];

const GrowthChartWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        noDataMessage: {
            'ui:widget': 'ResourcePicker',
        },
        chartTitles: headingsUISchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        transformData: { 'ui:widget': 'CustomCheckbox' },
        functionToTransform: { 'ui:widget': 'JSEditor' },
        dataKey: { 'ui:widget': 'ResourcePicker' },
        chartType: { 'ui:widget': 'CustomTextInput' },
        constructorType: { 'ui:widget': 'CustomTextInput' },
        showDateInput: { 'ui:widget': 'CustomCheckbox' },
        showInvestmentAmount: { 'ui:widget': 'CustomCheckbox' },
        showChartRange: { 'ui:widget': 'CustomCheckbox' },
        showTotalReturns: { 'ui:widget': 'CustomCheckbox' },
        showDifferentZone: { 'ui:widget': 'CustomCheckbox' },
        ...customSchemas.highchartOptionsSchema.HighchartOptionsSchema.uiSchema,
        growthChartAltText: { 'ui:widget': 'ResourcePicker' },
        rangeButtons: { 'ui:widget': 'CustomTextInput' },
        defaultSelectedRange: { 'ui:widget': 'CustomTextInput' },
        datePicker: { 'ui:widget': 'CustomSelect' },
        toolTipValueFormat: { 'ui:widget': 'CustomTextInput' },
        toolTipDateFormat: { 'ui:widget': 'CustomTextInput' },
        customTooltipOptions: { 'ui:widget': 'CustomCheckbox' },
        tooltipOptions: {
            items: {
                label: { 'ui:widget': 'ResourcePicker' },
                mappingkey: { 'ui:widget': 'CustomTextInput' },
                format: { 'ui:widget': 'CustomTextInput' },
                className: { 'ui:widget': 'CustomTextInput' },
                posChangeClassName: { 'ui:widget': 'CustomTextInput' },
                negChangeClassName: { 'ui:widget': 'CustomTextInput' },
                isValueKey: { 'ui:widget': 'CustomCheckbox' },
                seriesNumber: { 'ui:widget': 'CustomTextInput' },
                hideFirstTooltip: { 'ui:widget': 'CustomCheckbox' },
            },
        },
        isLimitToTenYears: { 'ui:widget': 'CustomCheckbox' },
        applyMaxDateValue: { 'ui:widget': 'CustomCheckbox' },
        applyCustomYaxisLabel: { 'ui:widget': 'CustomCheckbox' },
        yAxislabelFormat: { 'ui:widget': 'CustomTextInput' },
        xAxislabelFormat: { 'ui:widget': 'CustomTextInput' },
        growthAmountLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        initialGrowthAmount: { 'ui:widget': 'CustomTextInput' },
        totalReturnsLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        zoneOptions: {
            year: { 'ui:widget': 'CustomTextInput' },
            color: { 'ui:widget': 'CustomTextInput' },
        },
        datePickerOptions: {
            startDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            startDayAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            startMonthAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            startYearAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDayAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endMonthAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endYearAriaLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
        },
        isShowMultipleSeries: { 'ui:widget': 'CustomCheckbox' },
        useSimpleDataset: { 'ui:widget': 'CustomCheckbox' },
        yAxisValueOverride: { 'ui:widget': 'CustomCheckbox' },
        multipleSeriesMaxLength: { 'ui:widget': 'CustomTextInput' },
        multipleSeriesColors: { items: { 'ui:widget': 'CustomTextInput' } },
        integratedDatePickerOptions: {
            datePickerIconPosition: { 'ui:widget': 'CustomSelect' },
            datePickerIconType: { 'ui:widget': 'CustomSelect' },
            dateFormat: { 'ui:widget': 'CustomTextInput' },
            startDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            dataKey: { 'ui:widget': 'CustomTextInput' },
            datePickerIcon: { 'ui:widget': 'CustomTextInput' },
            datePickerImage: {
                'ui:placeholder': 'https://',
                'ui:widget': 'MediaPicker',
                'ui:options': {
                    isImagePicker: true,
                },
            },
        },
        customDatePickerOptions: {
            startDateFilterByKey: { 'ui:widget': 'CustomTextInput' },
            endDateFilterByKey: { 'ui:widget': 'CustomTextInput' },
            datePickerIconPosition: { 'ui:widget': 'CustomSelect' },
            datePickerIconType: { 'ui:widget': 'CustomSelect' },
            datePickerIcon: { 'ui:widget': 'CustomTextInput' },
            closeDatePickerIconPosition: { 'ui:widget': 'CustomSelect' },
            closeDatePickerIconType: { 'ui:widget': 'CustomSelect' },
            closeDatePickerIcon: { 'ui:widget': 'CustomTextInput' },
            backArrowIconType: { 'ui:widget': 'CustomSelect' },
            backArrowIcon: { 'ui:widget': 'CustomTextInput' },
            backArrowImage: { 'ui:widget': 'CustomTextInput' },
            forwardArrowIcon: { 'ui:widget': 'CustomTextInput' },
            forwardArrowImage: { 'ui:widget': 'CustomTextInput' },
            forwardArrowIconType: { 'ui:widget': 'CustomSelect' },
            startDatelabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDatelabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            limitStartDateMinToMinDate: { 'ui:widget': 'CustomCheckbox' },
            limitStartDateMaxToCurrentDate: { 'ui:widget': 'CustomCheckbox' },
            limitStartDateMaxToMaxDate: { 'ui:widget': 'CustomCheckbox' },
            limitEndDateMinToStartDate: { 'ui:widget': 'CustomCheckbox' },
            limitEndDateMaxToCurrentDate: { 'ui:widget': 'CustomCheckbox' },
            limitEndDateMaxToMaxDate: { 'ui:widget': 'CustomCheckbox' },
            limitCurrentDaySelection: { 'ui:widget': 'CustomCheckbox' },
            limitCurToMaxDate: { 'ui:widget': 'CustomCheckbox' },
            startDateErrorMessage: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDateErrorMessage: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            limitDateStartErrorMessage: {
                'ui:widget': 'ResourcePicker',
                'ui:placeholder': '{{ }}',
            },
            limitDateEndErrorMessage: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            errorIconPosition: { 'ui:widget': 'CustomSelect' },
            errorIconType: { 'ui:widget': 'CustomSelect' },
            errorIcon: { 'ui:widget': 'CustomTextInput' },
            errorImageUrl: { 'ui:widget': 'CustomTextInput' },
            dataKey: { 'ui:widget': 'CustomTextInput' },
            limitDatekeySelection: { 'ui:widget': 'CustomTextInput' },
        },
    },
    templateView: GrowthChartWrapper,
    id: 'GrowthChart',
    group: 'charts',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default GrowthChartWidget;
